/**
 * ChatMessage Component
 * Individual message bubble matching mockup design
 * - User messages: right-aligned with rounded-tr-none
 * - Assistant messages: left-aligned with avatar and rounded-tl-none
 */
import React from "react";
import {
  ChatMessage as ChatMessageType,
  MessageRole,
} from "../../types/chat.types";
import { format } from "date-fns";
import { JobResultCard } from "./JobResultCard";
import { Bot } from "lucide-react";

interface ChatMessageProps {
  message: ChatMessageType;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === MessageRole.USER;
  const isAssistant = message.role === MessageRole.ASSISTANT;
  
  // Check if message has job data to render
  const jobData = message.metadata?.job_data;
  const showImages = message.metadata?.show_images;
  
  // Check if user message has attached image
  const userImage = message.metadata?.uploaded_image;

  // AI Avatar
  const AI_AVATAR = (
    <div className="w-10 h-10 rounded-full bg-[#1a2b4b] border border-blue-500/30 flex items-center justify-center flex-shrink-0">
      <Bot className="w-5 h-5 text-blue-400" />
    </div>
  );

  // User message
  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-[80%]">
          {/* User uploaded image */}
          {userImage && (
            <div className="mb-2">
              <img
                src={userImage}
                alt="Uploaded"
                className="max-w-full rounded-xl max-h-48 object-cover ml-auto"
              />
            </div>
          )}
          
          {/* Message bubble */}
          <div className="bg-[#1a2b4b]/60 border border-[#2d4a7c] rounded-2xl rounded-tr-none p-5 text-slate-200 shadow-lg">
            <p className="leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </p>

            {/* Job metadata */}
            {message.metadata?.job_number && (
              <div className="mt-2 pt-2 border-t border-[#2d4a7c]/50">
                <p className="text-xs text-slate-400">
                  Job: {message.metadata.job_number}
                </p>
              </div>
            )}
          </div>
          
          {/* Timestamp */}
          <p className="text-[10px] text-slate-500 mt-1 text-right uppercase tracking-widest">
            {format(new Date(message.created_at), "HH:mm")}
          </p>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex gap-4 mb-4">
      <div className="flex-shrink-0">{AI_AVATAR}</div>
      <div className="flex-1 max-w-[85%]">
        {/* Message bubble */}
        <div className="bg-[#0f1a30] border border-white/5 rounded-2xl rounded-tl-none p-6 shadow-xl">
          <div className="space-y-3 text-slate-300">
            <p className="leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </p>

            {/* Job metadata */}
            {message.metadata?.job_number && (
              <div className="pt-2 border-t border-white/5">
                <p className="text-xs text-slate-400">
                  Job: {message.metadata.job_number}
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Job Result Card for specific job queries */}
        {isAssistant && jobData && showImages && (
          <div className="mt-3">
            <JobResultCard 
              job={jobData} 
              showImages={true} 
            />
          </div>
        )}
        
        {/* Timestamp */}
        <p className="text-[10px] text-slate-500 mt-2 uppercase tracking-widest">
          {format(new Date(message.created_at), "HH:mm")}
        </p>
      </div>
    </div>
  );
};
